import { createShortUid, logInfo, logWarn } from '@amazon/vinyl-util'
import { saveResponseEntities } from '@amzn/amp-gql'
import { getPlaybackEnv } from '@amzn/amp-playback'
import type { BridgedPlayback } from '@amzn/amp-playback/bridge'
import { createWebViewClientTransport } from '@amzn/amp-playback/web'
import { withdrawDeviceFeature } from '@/device/deviceConfig'
import { bindTransportMediaExclusivity } from '@/playback/mediaExclusivity'
import { ctx } from '@/util/context'
import { queryParam } from '@/util/queryParams'

export type PlaybackHostMode = 'local' | 'bridged'

export type PlaybackSelection =
    | { readonly mode: 'local' }
    | { readonly mode: 'bridged'; readonly bridged: BridgedPlayback }

export const LOCAL_PLAYBACK: PlaybackSelection = { mode: 'local' }

const { target } = ctx('playbackSelection')

/** `?playbackHost=local|bridged`, for debugging; null when absent or unknown. */
export function playbackHostOverride(): PlaybackHostMode | null {
    const value = queryParam('playbackHost')
    return value === 'local' || value === 'bridged' ? value : null
}

/**
 * Decides once, at boot, where playback runs for the app's lifetime. A playback host in the web
 * view's native shell plays when `?playbackHost=bridged` asks for it, until hosts can place
 * video and audiobooks on the client. Otherwise, and whenever the host refuses the handshake, is
 * too old or doesn't answer in time, playback stays local.
 */
export async function selectPlayback(): Promise<PlaybackSelection> {
    const override = playbackHostOverride()
    // The shell may only announce its host after boot, so asking for it skips the announcement and
    // lets the handshake find out.
    const transport =
        override === 'bridged'
            ? createWebViewClientTransport(window, { requireAnnouncement: false })
            : null
    if (!transport) {
        logInfo(
            target,
            override === 'bridged' ? 'local playback: not in a native web view' : 'local playback'
        )
        return LOCAL_PLAYBACK
    }
    try {
        const { createBridgedPlayback } = await import('@amzn/amp-playback/bridge')
        const bridged = await createBridgedPlayback({
            transport,
            env: getPlaybackEnv(),
            clientId: createShortUid(),
            adoptEntity: saveResponseEntities,
        })
        bindTransportMediaExclusivity(bridged.player)
        // The visualizer taps the web view's own audio element, which a host leaves silent.
        withdrawDeviceFeature('visualizer')
        logInfo(target, `bridged playback: the host plays ${bridged.capabilities.kinds.join(', ')}`)
        return { mode: 'bridged', bridged }
    } catch (error) {
        transport.dispose()
        logWarn(target, 'local playback: the playback host did not connect', error)
        return LOCAL_PLAYBACK
    }
}
